import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Modal } from '../modal';
import { Button } from '../button';
import LoadingSpinner from '../LoadingSpinner';
import { POLICY_PAGES, brandPolicyPath, brandPolicyTitle, getBrand } from '../../constants/brands';
import { useQuery } from '@apollo/client';
import { PolicyMarkdown, usePolicyMarkdown } from '../../containers/PolicyPage/policyMarkdown';
import { POLICY_DOCUMENT } from '../../services/policyService/queries';
import { POLICY_KIND } from '../../services/policyService/policyKinds';
import { policyVersionOf } from '../../utils/policyVersion';

/** What the server records for the acceptance (`applyToBeHost(hostAgreement:)`). */
export interface HostAgreementAcceptance {
  version: string;
  documentPath: string;
  platform: 'web';
  /** Sent only when the API has a published version id. */
  policyVersionId?: string;
}

const Title = styled.h3`
  ${tw`text-lg font-semibold mb-2 text-text-primary`}
  font-family: var(--font-display);
`;

const Intro = styled.p`
  ${tw`text-sm text-text-secondary mb-3`}
`;

/** The agreement scrolls inside the dialog so the checkbox and buttons stay in view. */
const AgreementBox = styled.div`
  ${tw`border border-border-default rounded-md px-4 py-3 overflow-y-auto bg-bg-secondary`}
  border-style: solid;
  max-height: min(50vh, 420px);

  /* The policy page typography is sized for a full page */
  h2 {
    ${tw`text-base mt-4 mb-2`}
  }
  h3 {
    ${tw`text-sm mt-3 mb-1`}
  }
  p,
  li {
    ${tw`text-sm`}
  }
`;

const OpenLink = styled.a`
  ${tw`inline-block text-sm text-black underline mt-2`}
`;

const CheckRow = styled.label`
  ${tw`flex items-start gap-2 mt-4 text-sm text-text-primary cursor-pointer`}

  input {
    ${tw`mt-1 flex-shrink-0`}
    width: 16px;
    height: 16px;
  }
`;

const ErrorText = styled.p`
  ${tw`text-sm text-red-600`}
`;

const Actions = styled.div`
  ${tw`flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5`}
`;

interface HostAgreementConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called once the person ticked the box and chose "Agree and apply". */
  onAgree: (acceptance: HostAgreementAcceptance) => void;
}

/**
 * Shown before a host application: the viewing brand's Host Agreement (Lenvoy's own, or
 * the default one for a brand without its own) and a required "I agree" box.
 */
const HostAgreementConsentModal: React.FC<HostAgreementConsentModalProps> = ({
  isOpen,
  onClose,
  onAgree,
}) => {
  const brand = getBrand();
  const title = brandPolicyTitle('host-agreement', brand);
  const documentPath = brandPolicyPath('host-agreement', brand);
  const { content, loading, error } = usePolicyMarkdown(isOpen ? documentPath : null, title);
  const published = useQuery(POLICY_DOCUMENT, {
    variables: { kind: POLICY_KIND.HOST_AGREEMENT },
    skip: !isOpen,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const policyVersionId = published.data?.policyDocument?.id;
  // The "Last updated" date of the text shown is the version the server records.
  const version = content ? policyVersionOf(content) : null;
  const missingVersion = !loading && !error && !!content && !version;
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (isOpen) setAgreed(false);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      modalWidth="min(680px, calc(100vw - 2rem))"
    >
      <Title>Before you apply</Title>
      <Intro>
        Hosting on {brand.name} is governed by the {brand.name} {title}. Please read it, then
        confirm that you agree.
      </Intro>
      <AgreementBox data-testid="host-agreement-text">
        {loading && <LoadingSpinner />}
        {error && <ErrorText>{error}</ErrorText>}
        {missingVersion && (
          <ErrorText>
            This agreement has no date, so it cannot be accepted. Please contact support.
          </ErrorText>
        )}
        {!loading && !error && <PolicyMarkdown content={content} />}
      </AgreementBox>
      <OpenLink
        href={POLICY_PAGES['host-agreement'].route}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open the {title} in a new tab
      </OpenLink>
      <CheckRow>
        <input
          type="checkbox"
          checked={agreed}
          onChange={e => setAgreed(e.target.checked)}
          data-testid="host-agreement-checkbox"
        />
        <span>
          I have read and agree to the {brand.name} {title}.
        </span>
      </CheckRow>
      <Actions>
        <Button text="Cancel" theme="secondary" type="button" onClick={onClose} />
        <Button
          text="Agree and apply"
          type="button"
          isDisabled={!agreed || loading || !!error || !version}
          onClick={() =>
            version &&
            onAgree({
              version,
              documentPath,
              platform: 'web',
              ...(typeof policyVersionId === 'string' && policyVersionId
                ? { policyVersionId }
                : {}),
            })
          }
          dataTestId="host-agreement-agree"
        />
      </Actions>
    </Modal>
  );
};

export default HostAgreementConsentModal;
